import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import Button from '~components/Button/Button';
import { useAccessibleTextColor } from '~globals/common';

const TabContent = ({
    bgColor,
    children,
    ctalink,
    isActive,
    question,
    title,
    trackingName,
}: React.PropsWithChildren<{
    bgColor: SupportedBackgroundColor;
    ctalink: string;
    isActive: boolean;
    question: string;
    title: string;
    trackingName: string;
}>) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const [maxHeight, setMaxHeight] = useState(0);
    const childrenContainer = useRef<HTMLDivElement>(null);

    const htmlStrippedTitle = title.replace(/<.+>/g, ' ');
    const walink = `${trackingName}: ${htmlStrippedTitle}: Learn More`;

    useEffect(() => {
        const { current: container } = childrenContainer;
        setIsExpanded(false);
        if (container) {
            const calcMax = 3 * parseFloat(getComputedStyle(container).lineHeight);
            if (container.clientHeight > calcMax) {
                setCanExpand(true);
                setMaxHeight(calcMax);
            } else {
                setCanExpand(false);
            }
        }
    }, [isActive]);

    return (
        <article
            className={classNames(
                !isActive && 'hidden',
                'w-full flex flex-col',
                'mb-4 px-4 pt-4 lg:mb-0 lg:px-0 lg:pt-0'
            )}
        >
            <h3
                className={classNames(
                    'mb-6 font-bold text-32',
                    useAccessibleTextColor(bgColor, 'text-primary-dark')
                )}
            >
                {question}
            </h3>
            <div>
                <div
                    ref={childrenContainer}
                    className={classNames('overflow-y-hidden', useAccessibleTextColor(bgColor))}
                    style={{
                        maxHeight:
                            !isExpanded && canExpand && childrenContainer.current && maxHeight > 0
                                ? `${maxHeight}px`
                                : undefined,
                    }}
                >
                    {children}
                </div>
                {canExpand && (
                    <button
                        className={classNames(
                            'font-bold text-18',
                            useAccessibleTextColor(bgColor, 'text-primary-dark')
                        )}
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {!isExpanded ? 'See more...' : 'Show less'}
                    </button>
                )}
            </div>
            <Button
                islink={true}
                to={ctalink}
                data-wa-link={walink}
                styletype="primary"
                className="w-max mt-6"
            >
                Learn more
            </Button>
        </article>
    );
};

export default TabContent;
